import React, { useState, useEffect, useCallback } from "react";
import { X, Loader2, Save, AlertCircle, Wand2 } from "lucide-react";
import {
  PROJECT_TYPES,
  PROJECT_DIVISIONS,
  PROJECT_LISTING_STATUSES,
  URGENCY_OPTIONS,
  MATERIALS_OPTIONS,
  LICENSE_OPTIONS,
  OCCUPIED_OPTIONS,
  CONTACT_METHODS,
  generateProjectListingTemplate,
} from "@/data/projectListingTemplates";

const BLANK = {
  title: "",
  project_type: "residential",
  division: "drywall",
  city: "",
  service_area: "",
  start_date: "",
  duration: "",
  urgency: "Standard",
  overview: "",
  scope_needed: "",
  requirements: "",
  timeline_notes: "",
  location_notes: "",
  materials_provided: "Partial",
  labor_only: false,
  insurance_required: true,
  license_required: "If applicable",
  crew_size: "",
  tools_equipment: "",
  access_notes: "",
  work_hours: "",
  occupied_or_vacant: "Occupied",
  safety_requirements: "",
  bid_due_date: "",
  site_walk_required: false,
  contact_method: "Phone call",
  status: "open",
  is_active: true,
  is_featured: false,
  is_published: true,
  is_archived: false,
};

function Field({ label, htmlFor, children }) {
  return (
    <div>
      <label className="label-br" htmlFor={htmlFor}>{label}</label>
      {children}
    </div>
  );
}

function TextareaBlock({ label, value, onChange, name, rows = 4, placeholder }) {
  return (
    <div>
      <label className="label-br" htmlFor={`pl-${name}`}>{label}</label>
      <textarea id={`pl-${name}`} rows={rows} className="textarea-br" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={`pl-${name}`} />
    </div>
  );
}

/**
 * AdminProjectListingModal
 *
 * Create/edit modal for a project listing. Admin picks project type + division
 * + (optionally) city + urgency, then clicks “Generate from template” to
 * splat a clean overview / scope / requirements block into the long-form
 * fields. Admin can edit any field before publishing.
 */
export default function AdminProjectListingModal({ open, mode, initial, onClose, onSave }) {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...BLANK, ...initial } : BLANK);
      setError("");
    }
  }, [open, initial]);

  const update = (k) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : e?.target?.value;
    setForm((p) => ({ ...p, [k]: v }));
  };
  const updateField = useCallback((k) => (v) => setForm((p) => ({ ...p, [k]: v })), []);

  const handleGenerateTemplate = () => {
    const tpl = generateProjectListingTemplate({
      project_type: form.project_type,
      division: form.division,
      city: form.city,
      urgency: form.urgency,
    });
    setError("");
    setForm((p) => ({
      ...p,
      title: p.title || tpl.title,
      overview: tpl.overview,
      scope_needed: tpl.scope_needed,
      requirements: tpl.requirements,
      timeline_notes: tpl.timeline_notes,
      location_notes: tpl.location_notes,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setError("Title is required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onSave({ ...form, title: form.title.trim() });
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save listing.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[150] bg-br-black/70 backdrop-blur-sm flex items-start justify-center px-3 py-6 sm:py-10 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      data-testid="admin-project-listing-modal"
    >
      <div className="w-full max-w-3xl bg-white rounded-lg shadow-2xl my-auto">
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-gray-200">
          <div>
            <div className="br-eyebrow text-br-bronze">Project Listing</div>
            <h3 className="font-display text-xl tracking-widest uppercase mt-1">{mode === "edit" ? "Edit Listing" : "New Listing"}</h3>
          </div>
          <button type="button" onClick={onClose} className="p-2 text-gray-500 hover:text-br-black" aria-label="Close" data-testid="pl-modal-close">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5" data-testid="pl-modal-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Field label="Project Title *" htmlFor="pl-title">
                <input id="pl-title" className="input-br" value={form.title} onChange={update("title")} required data-testid="pl-title" />
              </Field>
            </div>
            <Field label="Project Type *" htmlFor="pl-type">
              <select id="pl-type" className="select-br" value={form.project_type} onChange={update("project_type")} required data-testid="pl-type">
                {PROJECT_TYPES.map((t) => <option key={t.slug} value={t.slug}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Division *" htmlFor="pl-division">
              <select id="pl-division" className="select-br" value={form.division} onChange={update("division")} required data-testid="pl-division">
                {PROJECT_DIVISIONS.map((d) => <option key={d.slug} value={d.slug}>{d.label}</option>)}
              </select>
            </Field>
            <Field label="City" htmlFor="pl-city"><input id="pl-city" className="input-br" value={form.city} onChange={update("city")} placeholder="Austin, Round Rock…" data-testid="pl-city" /></Field>
            <Field label="Service Area" htmlFor="pl-area"><input id="pl-area" className="input-br" value={form.service_area} onChange={update("service_area")} placeholder="e.g. Central Texas" data-testid="pl-area" /></Field>
            <Field label="Estimated Start" htmlFor="pl-start"><input id="pl-start" className="input-br" value={form.start_date} onChange={update("start_date")} placeholder="e.g. Mid-June" data-testid="pl-start" /></Field>
            <Field label="Estimated Duration" htmlFor="pl-duration"><input id="pl-duration" className="input-br" value={form.duration} onChange={update("duration")} placeholder="e.g. 1–2 weeks" data-testid="pl-duration" /></Field>
            <Field label="Urgency" htmlFor="pl-urgency">
              <select id="pl-urgency" className="select-br" value={form.urgency} onChange={update("urgency")} data-testid="pl-urgency">
                {URGENCY_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </Field>
            <Field label="Status" htmlFor="pl-status">
              <select id="pl-status" className="select-br" value={form.status} onChange={update("status")} data-testid="pl-status">
                {PROJECT_LISTING_STATUSES.map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
              </select>
            </Field>
          </div>

          {/* Flags */}
          <div className="flex flex-wrap gap-x-6 gap-y-2 border-y border-gray-100 py-3">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" className="accent-br-bronze h-4 w-4" checked={!!form.is_active} onChange={update("is_active")} data-testid="pl-active" /> Active
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" className="accent-br-bronze h-4 w-4" checked={!!form.is_featured} onChange={update("is_featured")} data-testid="pl-featured" /> Featured
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" className="accent-br-bronze h-4 w-4" checked={!!form.is_published} onChange={update("is_published")} data-testid="pl-published" /> Published
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" className="accent-br-bronze h-4 w-4" checked={!!form.is_archived} onChange={update("is_archived")} data-testid="pl-archived" /> Archived
            </label>
          </div>

          {/* Smart template */}
          <div className="bg-br-paper border border-br-bronze/30 rounded p-3 flex items-start gap-3" data-testid="pl-template-row">
            <Wand2 size={16} className="text-br-bronze mt-1 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-sm text-br-black">Smart-fill listing copy</div>
              <p className="text-xs text-gray-600 mt-0.5">
                Click “Generate from template” to autofill overview, scope, requirements, timeline, and location notes based on the selected project type + division. Edit before publishing.
              </p>
            </div>
            <button type="button" onClick={handleGenerateTemplate} className="br-btn-dark text-[11px] whitespace-nowrap" data-testid="pl-generate-template">
              <Wand2 size={12} /> Generate from template
            </button>
          </div>

          {/* Long-form */}
          <TextareaBlock label="Project Overview" value={form.overview} onChange={updateField("overview")} name="overview" rows={3} />
          <TextareaBlock label="Scope Needed" value={form.scope_needed} onChange={updateField("scope_needed")} name="scope_needed" rows={4} />
          <TextareaBlock label="Trade Partner Requirements" value={form.requirements} onChange={updateField("requirements")} name="requirements" rows={4} placeholder="One per line, bullets ok" />
          <TextareaBlock label="Timeline Notes" value={form.timeline_notes} onChange={updateField("timeline_notes")} name="timeline_notes" rows={2} />
          <TextareaBlock label="Location Notes" value={form.location_notes} onChange={updateField("location_notes")} name="location_notes" rows={2} />

          {/* Structured details */}
          <div className="grid sm:grid-cols-2 gap-4 border-t border-gray-100 pt-4">
            <Field label="Materials Provided by Black Ridge" htmlFor="pl-materials">
              <select id="pl-materials" className="select-br" value={form.materials_provided} onChange={update("materials_provided")} data-testid="pl-materials">
                {MATERIALS_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="License Required" htmlFor="pl-license">
              <select id="pl-license" className="select-br" value={form.license_required} onChange={update("license_required")} data-testid="pl-license">
                {LICENSE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Crew Size Needed" htmlFor="pl-crew"><input id="pl-crew" className="input-br" value={form.crew_size} onChange={update("crew_size")} placeholder="e.g. 2–3" data-testid="pl-crew" /></Field>
            <Field label="Tools / Equipment Needed" htmlFor="pl-tools"><input id="pl-tools" className="input-br" value={form.tools_equipment} onChange={update("tools_equipment")} data-testid="pl-tools" /></Field>
            <Field label="Access Notes" htmlFor="pl-access"><input id="pl-access" className="input-br" value={form.access_notes} onChange={update("access_notes")} placeholder="Gate code, pets, schedule…" data-testid="pl-access" /></Field>
            <Field label="Work Hours" htmlFor="pl-hours"><input id="pl-hours" className="input-br" value={form.work_hours} onChange={update("work_hours")} placeholder="M–F 7–5…" data-testid="pl-hours" /></Field>
            <Field label="Occupied or Vacant" htmlFor="pl-occupied">
              <select id="pl-occupied" className="select-br" value={form.occupied_or_vacant} onChange={update("occupied_or_vacant")} data-testid="pl-occupied">
                {OCCUPIED_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Safety Requirements" htmlFor="pl-safety"><input id="pl-safety" className="input-br" value={form.safety_requirements} onChange={update("safety_requirements")} placeholder="PPE, fall protection…" data-testid="pl-safety" /></Field>
            <Field label="Bid Due Date" htmlFor="pl-bid"><input id="pl-bid" className="input-br" value={form.bid_due_date} onChange={update("bid_due_date")} placeholder="e.g. 2026-06-15" data-testid="pl-bid" /></Field>
            <Field label="Contact Method" htmlFor="pl-contact">
              <select id="pl-contact" className="select-br" value={form.contact_method} onChange={update("contact_method")} data-testid="pl-contact">
                {CONTACT_METHODS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            <div className="sm:col-span-2 flex gap-6">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" className="accent-br-bronze h-4 w-4" checked={!!form.labor_only} onChange={update("labor_only")} data-testid="pl-labor-only" /> Labor only
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" className="accent-br-bronze h-4 w-4" checked={!!form.insurance_required} onChange={update("insurance_required")} data-testid="pl-insurance" /> Insurance required
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" className="accent-br-bronze h-4 w-4" checked={!!form.site_walk_required} onChange={update("site_walk_required")} data-testid="pl-site-walk" /> Site walk required
              </label>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700 flex items-start gap-2" data-testid="pl-error">
              <AlertCircle size={14} className="mt-0.5 shrink-0" /> {error}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-end gap-3 pt-2 border-t border-gray-100">
            <button type="button" onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-widest text-gray-500" data-testid="pl-cancel">Cancel</button>
            <button type="submit" disabled={saving} className="br-btn-primary" data-testid="pl-save">
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
              {saving ? "Saving…" : mode === "edit" ? "Save Changes" : "Create Listing"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
